import React, { Suspense, lazy, useEffect, useRef, useState } from 'react'
import _ from 'lodash'
import { Button, Spin, message } from 'antd'
import PhotoRow from './PhotoRow'
import ScannerErrorBoundary from '../../shared/camera/ScannerErrorBoundary'
import {
    MAX_IMAGES, MAX_VIDEOS, MAX_IMAGE_SIZE, MAX_VIDEO_SIZE, isImage, isVideo, countRowMedia, isPhotoRequested,
    BARCODE_RE, normalizeBarcode, orderNumberOf,
} from './mediaConstraints'
import { getRequestPhotoByBarcode } from '../../../services/api/3dApi'
import { updateRequestPhoto } from '../../../services/api/requestPhotoApi'
import { decodeImageCodes } from '../../shared/camera/decodeImageFile'
import '../styles/index.scss'

// Lazy-load: chunk camera + html5-qrcode (~80KB) chỉ tải khi user bấm "Scan".
const ScanCaptureModal = lazy(() => import('./ScanCaptureModal'))

// Mỗi dòng = 1 đơn hàng. id: khóa cục bộ tăng dần (không dùng index để tránh lỗi khi xóa dòng giữa).
const createRow = (id) => ({
    id,
    barcode: '',          // mã đơn đã chuẩn hóa, ví dụ '#123456_1'
    orderData: null,      // payload từ getRequestPhotoByBarcode
    requestId: null,      // orderData._origin_request._id
    existingPhotos: [],   // ảnh đã có trên server (đã lưu)
    pendingFiles: [],     // [{ file, preview }] chờ upload
    error: null,          // lỗi riêng của dòng
    loading: false,       // đang fetch đơn (nhập tay)
    justUploaded: false,  // TRUE = vừa upload thành công trong phiên này (để phân biệt với đơn
                          // vốn đã ở trạng thái photo_updated từ trước → hiển thị alert phù hợp)
})

const isMobileDevice = () => typeof window.matchMedia === 'function'
    && window.matchMedia('(pointer: coarse)').matches

const RequestPhotoContainer = () => {
    const idSeq = useRef(0)
    const nextId = () => { idSeq.current += 1; return idSeq.current }

    const [rows, setRows] = useState(() => [createRow(0)]) // 1 dòng trống ban đầu
    const [activeRowId, setActiveRowId] = useState(null)   // dòng đang mở camera (1 camera/lần)
    const [scanMode, setScanMode] = useState('scan')       // 'scan' = quét QR (nhận/đổi đơn); 'capture' = chụp luôn
    const [focusRowId, setFocusRowId] = useState(null)     // dòng cần focus ô nhập (PC, sau khi thêm đơn)
    const [saving, setSaving] = useState(false)
    const [hasCamera, setHasCamera] = useState(false)
    const [cameraBlocked, setCameraBlocked] = useState(false) // camera bị chặn/từ chối → hiện fallback
    const rowsRef = useRef(rows)

    useEffect(() => { rowsRef.current = rows }, [rows])

    // Giới hạn số toast hiển thị cùng lúc (kết hợp CSS xếp chồng) để không trải dài màn hình.
    useEffect(() => { message.config({ maxCount: 5 }) }, [])

    // PC: tự focus ô nhập của dòng đầu khi vào màn (mobile dùng camera nên bỏ qua, tránh bật bàn phím).
    useEffect(() => {
        if (!isMobileDevice()) setFocusRowId(0)
    }, [])

    // Feature detection: chỉ hiện nút "Scan" khi thiết bị thật sự có camera.
    useEffect(() => {
        let active = true
        const md = navigator.mediaDevices
        const hasGetUserMedia = !!(md && typeof md.getUserMedia === 'function')
        // Mobile (đối tượng chính, gần như luôn có camera sau): cứ hiện nút nếu có getUserMedia;
        // lỗi "không có camera" đã được modal xử lý tại chỗ. Desktop dùng enumerateDevices.
        const isMobile = typeof window.matchMedia === 'function'
            && window.matchMedia('(pointer: coarse)').matches
        if (hasGetUserMedia && isMobile) {
            setHasCamera(true)
            return () => { active = false }
        }
        if (md && typeof md.enumerateDevices === 'function') {
            md.enumerateDevices()
                .then((devices) => { if (active) setHasCamera(devices.some(d => d.kind === 'videoinput')) })
                .catch(() => {})
        }
        return () => { active = false }
    }, [])

    // Revoke toàn bộ preview URL khi unmount để tránh leak blob.
    useEffect(() => {
        return () => {
            rowsRef.current.forEach(r => r.pendingFiles.forEach(f => f.preview && URL.revokeObjectURL(f.preview)))
        }
    }, [])

    const _patchRow = (id, patch) => setRows(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)))

    // ---- Quản lý dòng ----
    const _addRow = () => {
        const id = nextId()
        setRows(prev => [...prev, createRow(id)])
        // Mobile (có camera): mở camera chế độ QUÉT cho dòng mới → scan QR/barcode để nhận diện đơn.
        // PC (kể cả có webcam): focus ô nhập để gõ/scan bằng máy quét — KHÔNG tự bật webcam (phiền);
        // vẫn còn nút Scan để bấm thủ công nếu muốn.
        if (hasCamera && isMobileDevice()) {
            setScanMode('scan')
            setActiveRowId(id)
        } else {
            setFocusRowId(id)
        }
    }

    // So theo SỐ ĐƠN (bỏ hậu tố item _1/_2...) — mỗi dòng là 1 ĐƠN: hai item cùng đơn (10024693_1
    // và 10024693_2) ở hai dòng khác nhau bị coi là TRÙNG đơn.
    const _isDuplicateBarcode = (value, exceptRowId) => {
        const order = orderNumberOf(value)
        return rowsRef.current.some(r => r.id !== exceptRowId && r.requestId && orderNumberOf(r.barcode) === order)
    }

    const _removeRow = (id) => {
        setRows(prev => {
            const removed = prev.find(r => r.id === id)
            if (removed) removed.pendingFiles.forEach(f => f.preview && URL.revokeObjectURL(f.preview))
            // Luôn giữ ít nhất 1 dòng: nếu xóa dòng cuối cùng → đặt lại thành dòng trống mới.
            if (prev.length <= 1) return [createRow(nextId())]
            return prev.filter(r => r.id !== id)
        })
    }

    // ---- Camera (1 camera tại 1 thời điểm) ----
    // Scan: mở pha quét QR (nhận đơn mới HOẶC quét đơn khác để thay thế đơn hiện tại).
    const _openScan = (rowId) => { setScanMode('scan'); setActiveRowId(rowId) }
    // Capture: vào thẳng pha chụp (dòng đã có đơn) để thêm ảnh/video.
    const _openCapture = (rowId) => { setScanMode('capture'); setActiveRowId(rowId) }
    const _closeScan = () => setActiveRowId(null)
    // Lỗi truy cập camera → đóng modal + toast (thay vì hiện modal lỗi).
    // Không gộp key → các toast hiện nhiều cái nhưng xếp chồng lên nhau (CSS) + tối đa 5 (config).
    // Đồng thời bật cờ cameraBlocked: toast biến mất nhanh, nên giữ thêm hint CỐ ĐỊNH trên dòng
    // hướng dẫn dùng "Chọn file" + ảnh barcode để xác định đơn (fallback khi camera bị chặn,
    // ví dụ prod gửi Permissions-Policy: camera=() trên Android Chrome) — tránh để user vào ngõ cụt.
    const _onCameraError = (msg) => {
        setActiveRowId(null)
        setCameraBlocked(true)
        message.warning(msg)
    }

    // Gắn dữ liệu đơn vào 1 row object (dùng chung cho gán dòng có sẵn lẫn tạo dòng mới).
    const _rowWithOrder = (row, payload, barcode) => ({
        ...row,
        // Gắn _barcode vào orderData ở MỌI đường nhận đơn (quét/đọc-ảnh/nhập tay) để consumer
        // (vd _onIdentified đọc orderData._barcode) luôn nhất quán, không phụ thuộc đường vào.
        orderData: { ...payload, _barcode: barcode },
        requestId: payload._origin_request && payload._origin_request._id,
        barcode,
        existingPhotos: (payload.request_photo && payload.request_photo.photos) || [],
        error: null,
        loading: false,
        justUploaded: false, // vừa nhận diện/đổi đơn → chưa upload trong phiên
    })

    // Gán đơn vào dòng đã có (dùng chung cho quét camera lẫn nhập tay).
    // Nếu thay bằng đơn KHÁC (mã khác) → dọn các ảnh/video đang chờ của đơn cũ (tránh upload nhầm đơn).
    const _applyOrder = (rowId, payload, barcode) => setRows(prev => prev.map(r => {
        if (r.id !== rowId) return r
        // Đổi sang ĐƠN khác (khác số đơn) → dọn ảnh chờ. Cùng đơn khác item (_1 -> _2) → giữ ảnh chờ.
        const changingOrder = r.requestId && orderNumberOf(r.barcode) !== orderNumberOf(barcode)
        if (changingOrder) r.pendingFiles.forEach(f => f.preview && URL.revokeObjectURL(f.preview))
        return _rowWithOrder({ ...r, pendingFiles: changingOrder ? [] : r.pendingFiles }, payload, barcode)
    }))

    // Trả về true nếu đơn được nhận VÀ cho phép chụp (modal dùng để quyết định chuyển sang pha chụp).
    // Dùng cho luồng QUÉT QR TRỰC TIẾP (live scan). Luồng đọc-từ-ảnh xử lý trong _addFilesToRow.
    const _onIdentified = (orderData) => {
        if (activeRowId === null) return false
        const barcode = orderData._barcode || ''
        const allowed = isPhotoRequested(orderData)
        // Chặn trùng (phòng khi modal chưa lọc): không gán, báo lỗi ở dòng đang quét.
        if (_isDuplicateBarcode(barcode, activeRowId)) {
            _patchRow(activeRowId, { error: 'Đơn này đã có ở dòng khác' })
            return false
        }
        _applyOrder(activeRowId, orderData, barcode)
        if (!allowed) message.warning('Đơn không ở trạng thái cần chụp ảnh — chỉ xem, không chụp/tải lên')
        return allowed
    }

    // Nhập tay mã đơn (PC không có camera, hoặc gõ/quét bằng máy scan vật lý).
    const _submitBarcode = async (rowId, rawValue) => {
        const value = (rawValue || '').trim()
        if (!value) { _patchRow(rowId, { error: 'Nhập vào mã đơn.' }); return }
        if (!BARCODE_RE.test(value)) {
            _patchRow(rowId, { error: 'Sai định dạng mã đơn, ví dụ 123456_1 hoặc #123456_1' })
            return
        }
        if (_isDuplicateBarcode(value, rowId)) {
            _patchRow(rowId, { error: 'Đơn này đã có ở dòng khác' })
            return
        }
        _patchRow(rowId, { loading: true, error: null })
        try {
            const { data } = await getRequestPhotoByBarcode(normalizeBarcode(value))
            const { success, data: payload, error } = data || {}
            if (!success || !payload) {
                _patchRow(rowId, { loading: false, error: error || 'Không tìm thấy mã đơn' })
                return
            }
            _applyOrder(rowId, payload, value)
        } catch (e) {
            _patchRow(rowId, { loading: false, error: e.message || 'Lỗi kết nối' })
        }
    }


    // Camera chụp/quay xong → thêm vào dòng đang mở (dòng đã nhận diện đơn qua pha scan).
    // Trả promise để modal chờ xử lý xong rồi mới mở khóa chụp (tránh chụp/flash chồng);
    // _addFilesToRow vẫn decode-lọc-barcode-lạ để chặn ảnh của đơn khác.
    const _onCapture = (file) => {
        if (activeRowId === null) return undefined
        return _addFilesToRow(activeRowId, [file])
    }

    // Validate + thêm files vào danh sách chờ của 1 dòng (đã biết existing/pending hiện tại).
    // barcodeFile: file ảnh barcode đã dùng nhận diện đơn → đánh dấu isBarcode (khóa không cho xóa).
    const _appendValidFiles = (id, files, existingPhotos, pendingFiles, barcodeFile) => {
        let { images, videos } = countRowMedia(existingPhotos, pendingFiles)
        const accepted = []
        for (const file of files) {
            const { type, size, name } = file
            if (isImage(type)) {
                if (size > MAX_IMAGE_SIZE) { message.error(`Ảnh "${name}" vượt 10MB`); continue }
                if (images >= MAX_IMAGES) { message.error('Đã đủ tối đa 20 ảnh cho đơn này'); continue }
                images += 1
                accepted.push(file)
            } else if (isVideo(type)) {
                if (size > MAX_VIDEO_SIZE) { message.error(`Video "${name}" vượt 500MB`); continue }
                if (videos >= MAX_VIDEOS) { message.error('Đã đủ tối đa 10 video cho đơn này'); continue }
                videos += 1
                accepted.push(file)
            } else {
                message.error(`File "${name}" không phải ảnh hoặc video`)
            }
        }
        if (accepted.length) {
            // Tạo blob URL CHỈ cho file đã qua validate → tránh leak từ file bị từ chối.
            const items = accepted.map(file => ({
                file,
                preview: URL.createObjectURL(file),
                isBarcode: !!barcodeFile && file === barcodeFile,
            }))
            setRows(prev => prev.map(r => (r.id === id ? { ...r, pendingFiles: [...r.pendingFiles, ...items] } : r)))
        }
    }

    // Lọc bỏ ảnh chứa barcode của ĐƠN KHÁC (an toàn — tránh trộn ảnh nhiều đơn vào 1 dòng).
    // So theo SỐ ĐƠN: ảnh chỉ chứa item cùng đơn (kể cả _1/_2 khác item) → giữ. Ảnh chứa BẤT KỲ
    // mã của đơn khác → bỏ. Ảnh không có mã hoặc video → giữ (không decode được).
    const _filterForeignBarcodes = async (files, ownBarcode) => {
        const own = orderNumberOf(ownBarcode)
        const allowed = []
        for (const file of files) {
            if (!isImage(file.type)) { allowed.push(file); continue }
            let codes = []
            try { codes = await decodeImageCodes(file) } catch (e) { codes = [] }
            const foreign = codes.find(c => BARCODE_RE.test(c) && orderNumberOf(c) !== own)
            if (foreign) {
                message.error(`Ảnh có mã đơn khác (${foreign}) — không thuộc đơn ${own}. Đã bỏ qua.`)
                continue
            }
            allowed.push(file)
        }
        return allowed
    }

    // ---- Thêm ảnh/video vào dòng ĐÃ nhận diện đơn (camera chụp/quay lẫn chọn file) ----
    // Nhận diện đơn nay LÀM Ở PHA SCAN (quét QR/barcode trực tiếp), KHÔNG detect từ ảnh nữa. Hàm này
    // chỉ thêm media vào dòng đã có đơn + ở trạng thái photo_requested; vẫn decode-lọc-barcode-lạ để
    // chặn ảnh của đơn khác lọt vào (áp dụng cho cả chụp camera lẫn chọn file).
    const _addFilesToRow = async (id, files) => {
        const row = rowsRef.current.find(r => r.id === id)
        if (!row) return
        if (!row.requestId) {
            message.error('Hãy scan mã đơn trước khi thêm ảnh/video.')
            return
        }
        if (!isPhotoRequested(row.orderData)) {
            message.error('Đơn không ở trạng thái cần chụp ảnh — không thể chụp/tải lên')
            return
        }
        const workingFiles = await _filterForeignBarcodes(files, row.barcode)
        if (!workingFiles.length) return
        _appendValidFiles(id, workingFiles, row.existingPhotos, row.pendingFiles, null)
    }

    const _removePending = (id, idx) => {
        setRows(prev => prev.map(r => {
            if (r.id !== id) return r
            // Đơn đã nhận diện nhưng KHÔNG ở trạng thái cho phép → chỉ-xem, không cho xóa.
            if (r.requestId && !isPhotoRequested(r.orderData)) return r
            const removed = r.pendingFiles[idx]
            if (removed && removed.isBarcode) return r // ảnh barcode bị khóa, không cho xóa lẻ
            if (removed && removed.preview) URL.revokeObjectURL(removed.preview)
            const pendingFiles = r.pendingFiles.filter((_, i) => i !== idx)
            // KHÔNG reset dòng khi xóa hết ảnh: đơn đã nhận diện qua SCAN (độc lập với ảnh) nên giữ
            // nguyên thông tin đơn để chụp/thêm tiếp — không bắt scan lại.
            return { ...r, pendingFiles }
        }))
    }

    // Ảnh đã lưu: hiện CHƯA có API xóa trên server → chỉ ẩn ở UI (giữ behavior cũ).
    // TODO(BE): bổ sung API xóa ảnh của request-photo rồi gọi tại đây.
    // Chỉ cho ẩn khi đơn còn ở trạng thái cho phép (photo_requested). Upload/lưu 1 lần, không sửa →
    // đơn đã chốt (không còn photo_requested) thì KHÔNG cho xóa.
    const _removeExisting = (id, idx) => {
        setRows(prev => prev.map(r => {
            if (r.id !== id) return r
            if (r.requestId && !isPhotoRequested(r.orderData)) return r
            return { ...r, existingPhotos: r.existingPhotos.filter((_, i) => i !== idx) }
        }))
    }

    // Re-fetch TOÀN BỘ thông tin đơn của 1 dòng + dọn pending (sau khi LƯU thành công):
    // cập nhật ảnh đã lưu, orderData/trạng thái (photo_requested → photo_updated...), requestId.
    const _refreshRow = async (id) => {
        const row = rowsRef.current.find(r => r.id === id)
        if (!row || !row.barcode) return
        try {
            const { data } = await getRequestPhotoByBarcode(normalizeBarcode(row.barcode))
            const { success, data: payload } = data || {}
            if (success && payload) {
                setRows(prev => prev.map(r => {
                    if (r.id !== id) return r
                    r.pendingFiles.forEach(f => f.preview && URL.revokeObjectURL(f.preview))
                    return {
                        ...r,
                        orderData: { ...payload, _barcode: r.barcode },
                        requestId: (payload._origin_request && payload._origin_request._id) || r.requestId,
                        existingPhotos: (payload.request_photo && payload.request_photo.photos) || [],
                        pendingFiles: [],
                        error: null,
                        justUploaded: true, // _refreshRow chỉ gọi sau upload thành công
                    }
                }))
            }
        } catch (e) {
            // Upload đã OK; refresh lỗi — bỏ qua, lần LƯU/scan sau sẽ đồng bộ.
        }
    }

    // ---- LƯU: upload pending của 1 dòng (dùng chung cho LƯU tất cả lẫn LƯU 1 dòng) ----
    // Contract upload nằm DUY NHẤT ở đây (field 'source'='1C', field 'photos') để không lệch giữa 2 nơi.
    const _buildFormData = (pendingFiles) => {
        const formData = new FormData()
        formData.append('source', '1C')
        pendingFiles.forEach(f => formData.append('photos', f.file, f.file.name))
        return formData
    }

    const _extractError = (e) => (e.response && e.response.data && e.response.data.error) || e.message || 'Lưu thất bại'

    // Upload pending của 1 dòng. Ném lỗi nếu thất bại (để caller báo lỗi/giữ modal).
    // Thành công → DỌN pendingFiles NGAY (revoke preview), KHÔNG chờ _refreshRow: nếu refetch sau đó
    // lỗi (mạng chập chờn), pending đã sạch nên bấm LƯU lần nữa KHÔNG upload trùng.
    const _uploadRow = async (row) => {
        const { success, message: apiError } = await updateRequestPhoto(row.requestId, _buildFormData(row.pendingFiles))
        if (!success) throw new Error(apiError)
        setRows(prev => prev.map(r => {
            if (r.id !== row.id) return r
            r.pendingFiles.forEach(f => f.preview && URL.revokeObjectURL(f.preview))
            return { ...r, pendingFiles: [], error: null, justUploaded: true }
        }))
    }

    // ---- LƯU: upload theo lô tất cả dòng ----
    const _onSaveAll = async () => {
        const targets = rowsRef.current.filter(r => r.requestId && r.pendingFiles.length > 0)
        if (!targets.length) {
            message.warning('Chưa có ảnh/video nào để lưu')
            return
        }
        setSaving(true)
        let okCount = 0
        for (const row of targets) {
            try {
                await _uploadRow(row)
                okCount += 1
                await _refreshRow(row.id) // đồng bộ ảnh đã lưu + trạng thái (pending đã dọn ở _uploadRow)
            } catch (e) {
                _patchRow(row.id, { error: `Lưu thất bại: ${_extractError(e)}` })
            }
        }
        setSaving(false)
        if (okCount) {
            message.success(`Đã lưu ${okCount}/${targets.length} đơn`)
            // PC: lưu xong → focus ô nhập của dòng CHƯA nhận diện đầu tiên để nhập/scan đơn tiếp
            // (mobile bỏ qua để không bật bàn phím). Reset null trước để chắc chắn kích hoạt lại focus
            // kể cả khi trùng dòng đang set.
            if (!isMobileDevice()) {
                const nextEmpty = rowsRef.current.find(r => !r.requestId)
                if (nextEmpty) {
                    setFocusRowId(null)
                    window.setTimeout(() => setFocusRowId(nextEmpty.id), 0)
                }
            }
        }
    }

    // Lưu ngay 1 dòng (dùng cho nút LƯU trong khung camera).
    const _saveRow = async (id) => {
        const row = rowsRef.current.find(r => r.id === id)
        if (!row || !row.requestId || !row.pendingFiles.length) return
        setSaving(true)
        try {
            await _uploadRow(row)
            await _refreshRow(id)
            message.success('Đã lưu lên hệ thống')
            setActiveRowId(null) // lưu xong → đóng modal chụp ảnh
        } catch (e) {
            message.error(`Lưu thất bại: ${_extractError(e)}`) // giữ modal mở để thử lại
        } finally {
            setSaving(false)
        }
    }

    const scanOpen = activeRowId !== null
    const activeRow = scanOpen ? rows.find(r => r.id === activeRowId) : null
    // Ghi chú của đơn (nếu có) — hiển thị trong modal chụp sau khi scan xong.
    const activeNote = _.get(activeRow, 'orderData._origin_request.related_data.requested.note', '')
    // Chỉ cho LƯU khi có ÍT NHẤT 1 đơn hợp lệ (photo_requested) đang có ảnh/video chờ — khớp điều
    // kiện lọc trong _onSaveAll. Không có đơn nào upload được → disable nút để tránh bấm vô ích.
    const canSaveAny = rows.some(r => r.requestId && isPhotoRequested(r.orderData) && r.pendingFiles.length > 0)

    return (
        <Spin spinning={saving}>
            <div className="RequestPhotoContainer">
                <div className="container-fluid p-4">
                    <h1 className="mb-3">Upload ảnh hoặc video, mỗi dòng ứng với 1 đơn hàng (barcode)</h1>

                    {rows.map((row, i) => (
                        <PhotoRow
                            key={row.id}
                            row={row}
                            index={i}
                            hasCamera={hasCamera}
                            cameraBlocked={cameraBlocked}
                            disabled={saving}
                            autoFocusBarcode={focusRowId === row.id}
                            onOpenScan={_openScan}
                            onOpenCapture={_openCapture}
                            onSubmitBarcode={_submitBarcode}
                            onAddFiles={_addFilesToRow}
                            onRemovePending={_removePending}
                            onRemoveExisting={_removeExisting}
                            onRemoveRow={_removeRow}
                        />
                    ))}

                    <div className="d-flex justify-content-between align-items-center mt-4">
                        <Button icon="plus" onClick={_addRow} disabled={saving}>Thêm đơn</Button>
                        <Button type="primary" icon="save" loading={saving} disabled={saving || !canSaveAny} onClick={_onSaveAll}>LƯU</Button>
                    </div>

                    {scanOpen && (
                        <ScannerErrorBoundary onClose={_closeScan}>
                            <Suspense fallback={null}>
                                <ScanCaptureModal
                                    skipScan={scanMode === 'capture'}
                                    isDuplicate={(v) => _isDuplicateBarcode(v, activeRow ? activeRow.id : null)}
                                    onIdentified={_onIdentified}
                                    onCapture={_onCapture}
                                    onClose={_closeScan}
                                    onError={_onCameraError}
                                    pendingFiles={activeRow ? activeRow.pendingFiles : []}
                                    onRemovePending={(idx) => activeRow && _removePending(activeRow.id, idx)}
                                    onSaveRow={() => activeRow && _saveRow(activeRow.id)}
                                    saving={saving}
                                    detecting={!!(activeRow && activeRow.loading)}
                                    needBarcode={!(activeRow && activeRow.requestId)}
                                    orderCode={activeRow && activeRow.requestId ? `#${orderNumberOf(activeRow.barcode)}` : ''}
                                    note={activeNote}
                                />
                            </Suspense>
                        </ScannerErrorBoundary>
                    )}
                </div>
            </div>
        </Spin>
    )
}

export default RequestPhotoContainer
